import { createFeatureSelector, createSelector } from '@ngrx/store';
import { BusinessCard, BusinessCardImage } from '@planone/bau365/util-user-data';
import { selectQueryParams } from '@planone/shared/util-ngrx';

import { ProfileCompletionMode, Synonym } from './data-access-user.models';
import { DATA_ACCESS_USER_FEATURE_KEY, State } from './data-access-user.reducer';

export const getDataAccessUserState = createFeatureSelector<State>(DATA_ACCESS_USER_FEATURE_KEY);

export const getAvailableProfessions = createSelector(getDataAccessUserState, (state: State) => state.professions);

export const getAvailableSynonyms = createSelector(getDataAccessUserState, (state: State) => state.synonyms);

export const getUserProfession = createSelector(getDataAccessUserState, (state: State) => state.profession);

export const getUserSpecialization = createSelector(getDataAccessUserState, (state: State) => state.specialization);

export const getUserSynonym = createSelector(getDataAccessUserState, (state: State) => state.synonym);

export const getProfileLoadingFailed = createSelector(
  getDataAccessUserState,
  (state: State) => state.profileLoadingFailed
);

export const getUserProfileTitle = createSelector(getDataAccessUserState, (state: State) => state.userProfileTitle);
export const getUserProfileShortDescription = createSelector(
  getDataAccessUserState,
  (state: State) => state.userProfileShortDescription
);

export const getUserProfile = createSelector(
  getUserProfileTitle,
  getUserProfileShortDescription,
  (title, shortDescription) => {
    return { title, shortDescription };
  }
);

export const getUserProfileCompletionRedirectUrl = createSelector(selectQueryParams, (queryParams) => {
  if (queryParams && queryParams['redirectUrl']) {
    return decodeURIComponent(queryParams['redirectUrl']);
  } else {
    return null;
  }
});

export const getUserProfileCompletionMode = createSelector(selectQueryParams, (queryParams) => {
  if (queryParams && queryParams['mode'] && ['initial', 'update'].includes(queryParams['mode'])) {
    return queryParams['mode'] as ProfileCompletionMode;
  } else {
    return null;
  }
});

export const getUserPersonalizationInfo = createSelector(
  getUserSpecialization,
  getUserSynonym,
  getAvailableSynonyms,
  (userSpecialization, userSynonym, availableSynonyms) => {
    const info: {
      specialization?: { id: string; label: string };
      synonym?: Synonym;
    } = {
      synonym: userSynonym,
    };
    if (userSpecialization) {
      const specialization = availableSynonyms.find(
        (specializationSynonym) => specializationSynonym.specialization === userSpecialization
      );
      if (specialization) {
        info.specialization = {
          id: specialization.specialization,
          label: specialization.label,
        };
      }
    }
    info.synonym = userSynonym;

    return info;
  }
);

export const getUserJobInformation = createSelector(getDataAccessUserState, (state: State) => state.userJobInformation);

export const getUserProfileImage = createSelector(
  getUserJobInformation,
  (jobinformation) => jobinformation as BusinessCardImage
);
export const getEmployeeCountOptions = createSelector(
  getDataAccessUserState,
  (state: State) => state.employeeCountOptions
);
export const getAreaOfInterestOptions = createSelector(
  getDataAccessUserState,
  (state: State) => state.areaOfInterestOptions
);
export const isProfileImageUploaded = createSelector(
  getDataAccessUserState,
  (state: State) => state.isProfileImageUploaded
);
export const isProfileImageUploadError = createSelector(
  getDataAccessUserState,
  (state: State) => state.profileImageUploadError
);

/** Can be used to hide features that are not suppose to be public yet */
export const getIsPlanoneUser = createSelector(getDataAccessUserState, (state: State) =>
  state.userRoles?.includes('planoneEmployee')
);

export const getProfileDeletionSubmitted = createSelector(
  getDataAccessUserState,
  (state: State) => state.deletionFormSubmitted
);

export const isBusinessCardLoaded = createSelector(getDataAccessUserState, (state: State) => state.businessCardLoaded);
export const getBusinessCard = createSelector(getDataAccessUserState, (state: State) => state.businessCard);
export const getBusinessCardId = createSelector(
  getBusinessCard,
  (businessCard: BusinessCard | undefined) => businessCard?.uuid
);
export const getBusinessCardFields = createSelector(getDataAccessUserState, (state: State) => state.businessCardFields);
export const getBusinessCardMandatoryFields = createSelector(getDataAccessUserState, (state: State) =>
  state.businessCardMandatoryFields.map(({ name }) => name)
);

export const getSendBusinessCardSuccess = (exhibitorId: string) =>
  createSelector(getDataAccessUserState, (state) => !!state.sendBusinessCardSuccess.find((id) => id === exhibitorId));
